<script lang="ts">
  import { onMount } from 'svelte';
  import { session } from '../lib/auth.svelte';
  import { PLANES, type PlanInfo } from '../lib/signup';
  import {
    fetchBillingStatus,
    startSubscriptionCheckout,
    WHATSAPP_LINK,
    type BillingStatus,
  } from '../lib/billing';

  let status = $state<BillingStatus | null>(null);
  let pagoAbierto = $state(false);
  let resultado = $state<'aceptado'|'rechazado'|null>(null);
  let error = $state<string|null>(null);

  onMount(() => {
    void (async () => {
      status = await fetchBillingStatus();
    })();
  });

  async function pagar(e: MouseEvent, plan: PlanInfo) {
    e.preventDefault();
    if (!session.value) return;
    error = null;
    pagoAbierto = true;
    try {
      const billingPlan = { slug: plan.slug, nombre: plan.nombre, precioMensualGtq: plan.precio };
      const { url } = await startSubscriptionCheckout(session.value.tenantId, billingPlan, session.value.usuario);
      window.open(url, '_blank');
    } catch (e: any) {
      error = e?.message === 'payments-disabled' 
        ? 'Pagos en línea no disponibles. Contacte por WhatsApp.'
        : 'Error al crear el pago. Intente de nuevo.';
      pagoAbierto = false;
      return;
    }
    // Auto-verificar cada 5s
    const interval = setInterval(async () => {
      const s = await fetchBillingStatus();
      if (s?.estado === 'active') {
        resultado = 'aceptado';
        pagoAbierto = false;
        clearInterval(interval);
      }
    }, 5000);
    setTimeout(() => clearInterval(interval), 180000);
  }

  const currentPlanSlug = $derived(status?.plan?.slug ?? null);
</script>

<div class="paywall-page">
  <main class="card">
    <h1>Su periodo de prueba terminó</h1>
    <p class="sub">Elija un plan. Se abrirá Recurrente en otra pestaña. Después de pagar, <strong>cierre esa pestaña</strong> y el sistema detecta el pago automáticamente.</p>

    {#if currentPlanSlug}
      <p class="current-hint">Plan actual: <strong>{status?.plan?.nombre}</strong></p>
    {/if}

    <div class="plans">
      {#each PLANES as p (p.slug)}
        <a
          class="plan-card"
          class:current={p.slug === currentPlanSlug}
          href="/billing/trial-expired"
          onclick={(e) => pagar(e, p)}
        >
          <div class="plan-head">
            <span class="plan-name">{p.nombre}</span>
            {#if p.slug === currentPlanSlug}
              <span class="plan-tag">Actual</span>
            {/if}
          </div>
          <div class="plan-price">
            <strong>Q{p.precio.toLocaleString('es-GT')}</strong>
            <span>/mes</span>
          </div>
          <ul class="plan-features">
            {#each p.modulos as m}
              <li>{m}</li>
            {/each}
          </ul>
          <span class="plan-btn">
            {#if p.slug === currentPlanSlug}
              Continuar con este plan
            {:else}
              Cambiar a este plan
            {/if}
          </span>
        </a>
      {/each}
    </div>

    {#if error}
      <div class="resultado fail">{error}</div>
    {:else if pagoAbierto}
      <div class="resultado esperando">⏳ Esperando pago... revise la pestaña de Recurrente. Si ya pagó, esto se actualiza solo.</div>
    {:else if resultado === 'aceptado'}
      <div class="resultado ok">✅ Pago aceptado — su cuenta está activa. <a href="/">Ir al sistema →</a></div>
    {:else if resultado === 'rechazado'}
      <div class="resultado fail">❌ Pago rechazado o no detectado. Intente de nuevo o use otra tarjeta.</div>
    {/if}

    <div class="foot-actions">
      <a class="secondary" href={WHATSAPP_LINK} target="_blank" rel="noopener">
        Contactar por WhatsApp
      </a>
    </div>

    <p class="fine">Visa, Mastercard y tarjetas locales · Cobro recurrente mensual · Cancele cuando quiera</p>
  </main>
</div>

<style>
  .paywall-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
    background: #f8fafc;
  }
  .card {
    max-width: 760px;
    width: 100%;
    background: #fff;
    border: 1px solid rgba(0,0,0,0.06);
    border-radius: 16px;
    padding: 40px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.03), 0 8px 24px rgba(0,0,0,0.04);
  }
  h1 {
    margin: 0 0 4px;
    font-size: 24px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.03em;
  }
  .sub {
    color: #64748b;
    font-size: 14px;
    margin: 0 0 8px;
  }
  .current-hint {
    font-size: 13px;
    color: #16a34a;
    margin: 8px 0 20px;
    padding: 8px 14px;
    background: #ecfdf5;
    border-radius: 8px;
    border: 1px solid rgba(15,118,110,0.1);
  }

  .plans {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin: 20px 0 24px;
  }
  @media (max-width: 700px) {
    .plans { grid-template-columns: 1fr; }
    .card { padding: 24px 20px; }
  }

  .plan-card {
    background: #fff;
    border: 1.5px solid rgba(0,0,0,0.08);
    border-radius: 14px;
    padding: 24px 20px;
    cursor: pointer;
    font: inherit;
    color: inherit;
    text-align: left;
    display: flex;
    flex-direction: column;
    transition: all 0.2s;
  }
  .plan-card:hover {
    border-color: #16a34a;
    box-shadow: 0 4px 16px rgba(15,118,110,0.08);
    transform: translateY(-2px);
  }
  .plan-card.current {
    border-color: #16a34a;
    border-width: 2px;
  }

  .plan-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
  }
  .plan-name {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
  }
  .plan-tag {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 8px;
    border-radius: 6px;
    background: #ecfdf5;
    color: #16a34a;
    border: 1px solid rgba(15,118,110,0.15);
  }

  .plan-price {
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(0,0,0,0.05);
  }
  .plan-price strong {
    font-size: 28px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.03em;
  }
  .plan-price span {
    font-size: 13px;
    color: #94a3b8;
  }

  .plan-features {
    list-style: none;
    margin: 0 0 20px;
    padding: 0;
    font-size: 12.5px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .plan-features li {
    color: #475569;
    padding-left: 20px;
    position: relative;
    line-height: 1.4;
  }
  .plan-features li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 3px;
    width: 13px;
    height: 13px;
    border-radius: 50%;
    background: #ecfdf5;
    border: 1.5px solid rgba(15,118,110,0.25);
  }
  .plan-features li::after {
    content: '';
    position: absolute;
    left: 3.5px;
    top: 6px;
    width: 6px;
    height: 4px;
    border-left: 1.5px solid #16a34a;
    border-bottom: 1.5px solid #16a34a;
    transform: rotate(-45deg);
  }

  .plan-btn {
    display: block;
    margin-top: auto;
    padding: 10px;
    text-align: center;
    border-radius: 8px;
    background: #1e293b;
    color: #fff;
    font-weight: 600;
    font-size: 13px;
    transition: background 0.15s;
  }
  .plan-card.current .plan-btn {
    background: #16a34a;
  }
  .plan-card:hover:not(:disabled) .plan-btn {
    background: #0f172a;
  }

  .foot-actions {
    margin-top: 16px;
  }
  .secondary {
    display: block;
    width: 100%;
    text-align: center;
    padding: 10px;
    border: 1.5px solid rgba(0,0,0,0.1);
    border-radius: 8px;
    color: #64748b;
    text-decoration: none;
    font-weight: 600;
    font-size: 13px;
    background: #fff;
    cursor: pointer;
    font-family: inherit;
  }
  .secondary:hover { color: #0f172a; border-color: rgba(0,0,0,0.2); }
  .secondary:disabled { opacity: 0.6; cursor: not-allowed; }

  .resultado {
    text-align: center;
    padding: 12px 16px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 12px;
  }
  .resultado.ok { background: #ecfdf5; color: #16a34a; border: 1px solid rgba(15,118,110,0.15); }
  .resultado.ok a { color: #16a34a; font-weight: 700; }
  .resultado.fail { background: #fef2f2; color: #dc2626; border: 1px solid rgba(220,38,38,0.15); }
  .resultado.esperando { background: #eff6ff; color: #1e40af; border: 1px solid rgba(37,99,235,0.12); }

  .fine {
    font-size: 12px;
    color: #94a3b8;
    margin-top: 16px;
    text-align: center;
  }
</style>
